import {
  Component,
  Inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { RecipeService } from '../../../services/recipe-services';
import { MatIconButton } from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { DecimalPipe } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { AddAttachmentsDialogData } from '../../model';
import { CONSTANTS_DETAILS } from '../../model';

const MAX_PER_PROTOTYPE = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

@Component({
  selector: 'app-add-attachments-dialog',
  imports: [
    MatDialogModule,
    TranslateModule,
    MatIconButton,
    MatButtonModule,
    DecimalPipe,
    MatDividerModule,
  ],
  templateUrl: './add-attachments-dialog.component.html',
  styleUrl: './add-attachments-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddAttachmentsDialogComponent {
  constructor(
    private _dialogRef: MatDialogRef<AddAttachmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public _data: AddAttachmentsDialogData,
    private _snackbarService: SnackbarService,
    private _translate: TranslateService,
    private _recipeService: RecipeService,
  ) {}

  public selectedFiles = signal<File[]>([]);
  public uploading = signal<boolean>(false);

  /**
   * Handles the file selection event with validations.
   *
   * @param {Event} evt
   * @return {*}
   * @memberof AddAttachmentsDialogComponent
   */
  public filesPicked(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const list = input.files;
    if (!list) return;

    const picked = Array.from(list);
    const current = this.selectedFiles();
    const allowedExtensions = CONSTANTS_DETAILS.ALLOWED_FILE_TYPES;
    const valid: File[] = [];

    const totalPickedFileSize = picked.reduce(
      (acc, file) => acc + file.size,
      0,
    );

    if (totalPickedFileSize > MAX_FILE_BYTES) {
      this._snackbarService.open({
        message: this._translate.instant('maxAttachmentsSizeReached'),
        type: 'error',
      });
      input.value = '';
      return;
    }

    for (const file of picked) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';

      if (!allowedExtensions.includes(ext)) {
        this._snackbarService.open({
          message: this._translate.instant('restrictedFileType'),
          type: 'error',
        });
        input.value = '';
        return;
      }

      if (current.some((f) => f.name === file.name)) {
        this._snackbarService.open({
          message: this._translate.instant('duplicateAttachment'),
          type: 'error',
        });
        input.value = '';
        return;
      }

      if (this._data?.existingNames?.has(file.name)) {
        this._snackbarService.open({
          message: this._translate.instant('duplicateAttachment'),
          type: 'error',
        });
        input.value = '';
        return;
      }

      valid.push(file);
    }

    if (valid.length + current.length > MAX_PER_PROTOTYPE) {
      this._snackbarService.open({
        message: this._translate.instant('maxAttachmentsReached'),
        type: 'error',
      });
      input.value = '';
      return;
    }

    this.selectedFiles.set([...current, ...valid]);
    input.value = '';
  }
  /**
   * Handles the file upload process.
   *
   * @return {*}
   * @memberof AddAttachmentsDialogComponent
   */
  public uploadFiles() {
    if (this.selectedFiles().length === 0) return;

    this.uploading.set(true);
    this._recipeService
      .updateVariantAttachments(
        this._data.recipeId,
        this._data.variantData.recipeVariantId ?? 0,
        this.selectedFiles(),
      )
      .then((response) => {
        if (!response) {
          this.uploading.set(false);
          return;
        }
        const codeNum = Number(response.statusCode);
        if (codeNum === 200 && response.isSuccess) {
          this.uploading.set(false);
          this._snackbarService.open({
            message: this._translate.instant('attachmentsUploaded'),
            type: 'success',
          });
          this._dialogRef.close(true);
        } else if (codeNum === 200 && !response.isSuccess) {
          this.uploading.set(false);
          this._snackbarService.open({
            message: response.message ? response.message : '',
            type: 'error',
          });
          this._dialogRef.close(true);
        } else if (codeNum !== 200 && !response.isSuccess) {
          this._snackbarService.open({
            message: response.message ? response.message : '',
            type: 'error',
          });
          this._dialogRef.close(true);
        } else if (codeNum !== 200 && response.isSuccess) {
          this._snackbarService.open({
            message: response.message ? response.message : '',
            type: 'error',
          });
          this._dialogRef.close(true);
        }
      })
      .catch(() => {
        this.uploading.set(false);
      });
  }

  public close() {
    this._dialogRef.close(false);
  }
  /**
   * Handles the drag over event.
   *
   * @param {DragEvent} event
   * @memberof AddAttachmentsDialogComponent
   */
  public dragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }
  /**
   * Handles the drop over event.
   *
   * @param {DragEvent} event
   * @memberof AddAttachmentsDialogComponent
   */
  public dropOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files.length) {
      const files = Array.from(event.dataTransfer.files);
      this.filesPicked({ target: { files } } as unknown as Event);
    }
  }
  /**
   * Removes a file from the selected files list.
   *
   * @param {File} file
   * @memberof AddAttachmentsDialogComponent
   */
  public removeFile(file: File) {
    this.selectedFiles.set(this.selectedFiles().filter((f) => f !== file));
  }
}
